// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { CardHeader } from '@/components/card-header';
import {
  ExpandLessRounded,
  ExpandMoreRounded,
} from '@/constants/hardfin-standard-icons';
import { useDisclosure } from '@/hooks/use-disclosure';
import { getProjectAssets } from '@/project/api/get-project-assets';
import { Project } from '@/project/api/types';
import { ProjectAssignmentDialog } from '@/project/components/project-assignment-dialog';
import { ProjectAssignmentTimeline } from '@/project/components/project-assignment-timeline';
import { formatNumber } from '@/utilities/number';
import {
  Box,
  Button,
  Collapse,
  Divider,
  IconButton,
  Paper,
  Typography,
  styled,
} from '@mui/material';
import { useSuspenseQuery } from '@tanstack/react-query';
import { Fragment, useState } from 'react';

const ProductLabel = styled(Typography)(({ theme }) => ({
  display: 'flex',
  whiteSpace: 'nowrap',
  '&::after': {
    content: '""',
    flex: 1,
    marginLeft: theme.spacing(1),
    alignSelf: 'center',
    marginTop: `calc(${theme.typography.subtitle2.fontSize} - 2px)`,
    borderBottom: `1px dotted ${theme.palette.text.secondary}`,
  },
}));

interface Props {
  project: Project;
}

export function ProjectAssetAssignmentCard(props: Props) {
  const dialog = useDisclosure();

  const [closedProductIds, setClosedProductIds] = useState<Set<string>>(
    new Set()
  );

  const [remeasureTriggers, setRemeasureTriggers] = useState<
    Record<string, boolean>
  >({});

  const { data } = useSuspenseQuery({
    queryKey: ['project', props.project.key, 'asset'],
    queryFn: () => getProjectAssets(props.project.key),
  });

  const hasDeltas = props.project.assignments.some((assignment) => {
    const delta = assignment.requestedCount - assignment.assignedCount;

    return Math.abs(delta) > 0;
  });

  const showDelta =
    hasDeltas &&
    (props.project.status === 'ACTIVE' ||
      props.project.status === 'DATE_RANGE_INCOMPLETE');

  const gridColumn = showDelta ? 'span 6' : 'span 5';
  const gridTemplateColumns = showDelta
    ? '1fr repeat(2, auto) min-content auto min-content'
    : '1fr repeat(2, auto) min-content auto';

  return (
    <Paper variant="outlined">
      <ProjectAssignmentDialog
        project={props.project}
        isOpen={dialog.isOpen}
        onClose={dialog.close}
      />
      <CardHeader title="Assets assigned to this project" variant="subtitle2">
        <Button variant="contained" color="secondary" onClick={dialog.open}>
          Assign
        </Button>
      </CardHeader>
      <Divider />
      <Box
        padding={2}
        rowGap={1}
        columnGap={2}
        display="grid"
        alignItems="center"
        gridTemplateColumns={gridTemplateColumns}
      >
        {props.project.assignments.length === 0 && (
          <Typography
            color="text.secondary"
            textAlign="center"
            gridColumn={gridColumn}
          >
            There are no assets assigned to this project
          </Typography>
        )}
        {props.project.assignments.map((assignment) => {
          const delta = assignment.requestedCount - assignment.assignedCount;
          const label = delta > 0 ? 'needed' : 'extra';
          const isOpen = !closedProductIds.has(assignment.id);
          const assets = data.assets.filter(
            ({ item }) => item.id === assignment.id
          );

          const handleClick = () => {
            setClosedProductIds((closedIds) => {
              const newClosedIds = new Set(closedIds);
              if (newClosedIds.has(assignment.id)) {
                newClosedIds.delete(assignment.id);
              } else {
                newClosedIds.add(assignment.id);
              }
              return newClosedIds;
            });
          };

          const handleCollapseEntered = () => {
            // Trigger chart remeasurement after collapse animation completes
            setRemeasureTriggers((prev) => ({
              ...prev,
              [assignment.id]: !prev[assignment.id],
            }));
          };

          return (
            <Fragment key={assignment.id}>
              <ProductLabel variant="subtitle2">{assignment.name}</ProductLabel>
              <Typography variant="body2" whiteSpace="nowrap">
                {formatNumber(assignment.assignedCount)} assigned
              </Typography>
              <Typography variant="body2" whiteSpace="nowrap">
                /
              </Typography>
              <Typography variant="body2" whiteSpace="nowrap">
                {formatNumber(assignment.requestedCount)} requested
              </Typography>
              {showDelta && (
                <Typography variant="body2" whiteSpace="nowrap">
                  ({formatNumber(Math.abs(delta))} {label})
                </Typography>
              )}
              <IconButton size="small" onClick={handleClick}>
                {isOpen ? (
                  <ExpandLessRounded fontSize="small" />
                ) : (
                  <ExpandMoreRounded fontSize="small" />
                )}
              </IconButton>
              <Collapse
                in={isOpen}
                sx={{ gridColumn: 'span 6' }}
                onEntered={handleCollapseEntered}
              >
                <ProjectAssignmentTimeline
                  project={props.project}
                  assets={assets}
                  triggerRemeasure={remeasureTriggers[assignment.id]}
                />
              </Collapse>
            </Fragment>
          );
        })}
      </Box>
    </Paper>
  );
}
